import { createContext, useContext, useReducer, ReactNode } from "react";
import { SessionState, SessionAction, AgentPathNode } from "../types";

const uuidv4 = () => crypto.randomUUID();

const initialState: SessionState = {
  sessionId: "",
  agentSlug: null,
  personaId: null,
  queryType: null,
  agentPath: [],
  channel: null,
  visitorPhone: null,
  transferPhone: null,
  phoneCaptured: false,
  callState: "idle",
  startedAt: null,
  endedAt: null,
  transcript: [],
  liveActions: [],
  livePassenger: null,
  isMuted: false,
  errorMessage: null,
};

function sessionReducer(state: SessionState, action: SessionAction): SessionState {
  switch (action.type) {
    case "INIT_SESSION":
      return { ...initialState, sessionId: action.sessionId, agentSlug: action.agentSlug };
    case "SET_PERSONA":
      return { ...state, personaId: action.personaId };
    case "AGENT_HANDOFF": {
      // Dedup: ignore if toAgent is already the last node (React StrictMode double-fire)
      if (state.agentPath[state.agentPath.length - 1]?.agent === action.toAgent) return state;
      const next: AgentPathNode = { agent: action.toAgent, condition: action.routingCondition, isWarmTransfer: action.isWarmTransfer };
      if (state.agentPath.length === 0) {
        // Seed the path. The first active agent has no predecessor, so it
        // becomes a single chip; a real transfer also carries fromAgent.
        const seed: AgentPathNode[] = action.fromAgent ? [{ agent: action.fromAgent }, next] : [next];
        return { ...state, agentPath: seed };
      }
      return { ...state, agentPath: [...state.agentPath, next] };
    }
    case "SET_QUERY":
      return { ...state, queryType: action.queryType };
    case "SET_CHANNEL":
      return { ...state, channel: action.channel };
    case "SUBMIT_PHONE":
      return { ...state, visitorPhone: action.visitorPhone, transferPhone: action.transferPhone, phoneCaptured: true };
    case "CALL_STATE_CHANGE":
      return {
        ...state,
        callState: action.state,
        startedAt: action.state === "in_call" && !state.startedAt ? Date.now() : state.startedAt,
        endedAt: (action.state === "ended" || action.state === "failed") ? Date.now() : state.endedAt,
        errorMessage: action.state !== "failed" ? null : state.errorMessage,
      };
    case "TRANSCRIPT_TURN": {
      // Upsert, not append. The adapter re-emits a growing utterance under one
      // id, so a turn already on screen is rewritten in place.
      const at = state.transcript.findIndex((t) => t.id === action.turn.id);
      if (at === -1) return { ...state, transcript: [...state.transcript, action.turn] };
      const next = state.transcript.slice();
      next[at] = { ...next[at], ...action.turn };
      return { ...state, transcript: next };
    }
    case "ADD_LIVE_ACTION":
      return { ...state, liveActions: [...state.liveActions, action.action] };
    case "SET_LIVE_PASSENGER":
      // Merge so a follow-up tool (e.g. baggage) augments the profile instead of replacing it.
      return { ...state, livePassenger: { ...(state.livePassenger ?? {}), ...action.passenger } };
    case "TOGGLE_MUTE":
      return { ...state, isMuted: !state.isMuted };
    case "SET_ERROR":
      return { ...state, errorMessage: action.message };
    case "RESET":
      return { ...initialState, sessionId: uuidv4() };
    default:
      return state;
  }
}

interface SessionContextValue {
  state: SessionState;
  dispatch: React.Dispatch<SessionAction>;
}

const SessionContext = createContext<SessionContextValue | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(sessionReducer, {
    ...initialState,
    sessionId: uuidv4(),
  });
  return <SessionContext.Provider value={{ state, dispatch }}>{children}</SessionContext.Provider>;
}

export function useSession() {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error("useSession must be used within SessionProvider");
  return ctx;
}
